import { useEffect, useState } from 'react'
import Cropper from './Cropper'
import { useImageCropContext } from './ImageCropProvider'
import { ZoomSlider } from './Sliders'
import Button from 'app/components/Button'
import { readFile } from 'lib/cropImage'

const ImageCropModalContent = ({
  currentImage,
  handleDone,
  handleDelete,
  handleCancel,
  shape,
  name,
  priority,
}: {
  currentImage?: string
  handleDone: (name: string, priority?: number) => void
  handleDelete: () => void
  handleCancel: () => void
  shape?: `round` | `rect`
  name: string
  priority?: number
}) => {
  const { image, setImage, setShape } = useImageCropContext()
  const [savedName, setSavedName] = useState(name)
  const [savedPriority, setSavedPriority] = useState(priority)
  const [isSaving, setIsSaving] = useState(false)

  const handleFileChange = async ({ target: { files } }) => {
    const file = files && files[0]
    const imageDataUrl = (await readFile(file)) as string
    setImage(imageDataUrl)
  }

  const handleNameChange = (name: string) => {
    setSavedName(name)
  }

  const onSaveClick = () => {
    setIsSaving(true)
    handleDone(savedName, savedPriority)
  }

  useEffect(() => {
    if (shape && setShape) {
      setShape(shape)
    }
  }, [shape])

  return (
    <div className="relative w-[480px] text-center">
      <div className="flex flex-col gap-6 p-6">
        <div className="flex flex-col items-start gap-1.5">
          <p className="text-body-sm text-default">Provider name*</p>
          <input
            className="h-10 w-full rounded-lg border border-default bg-surface px-4 text-body-sm"
            value={savedName}
            onChange={(e) => handleNameChange(e.target.value)}
          />
        </div>
        <div className="flex flex-col items-start gap-1.5">
          <p className="text-body-sm text-default">Agent priority</p>
          <p className="text-caption-sm text-subtle">Lower numbers are shown first (1 before 2 before 3)</p>
          <input
            type="number"
            min="1"
            className="h-10 w-24 rounded-lg border border-default bg-surface px-4 text-body-sm"
            value={savedPriority ?? ``}
            onChange={(e) => setSavedPriority(e.target.value ? Number(e.target.value) : undefined)}
          />
        </div>
        <div className="flex flex-col items-start">
          <p className="text-ui-md text-default">Profile picture</p>
          <p className="mt-1 text-body-sm text-default">For best results use a PNG or JPG at least 300 x 300 px</p>
        </div>
        {!!image && (!currentImage || currentImage !== image) ? (
          <div className="flex justify-center">
            <div className="crop-container relative mb-4 h-80 w-80">
              <Cropper />
            </div>
          </div>
        ) : (
          <>
            {!!currentImage && (
              <div className="flex items-center justify-center">
                <img src={currentImage} className="h-40 w-40 rounded-lg object-cover" alt="profile-img" />
              </div>
            )}
          </>
        )}
        <div className="flex w-full gap-2">
          {!!currentImage && (
            <Button mode="danger" className="grow" onClick={handleDelete}>
              Delete
            </Button>
          )}
          <Button mode="tertiary" className="grow">
            <label htmlFor="picInput2" className="mb-0 w-full cursor-pointer whitespace-nowrap">
              Upload new picture
            </label>
          </Button>
        </div>

        {!!image && <ZoomSlider className="my-4" />}
        <input type="file" multiple onChange={handleFileChange} className="hidden" id="picInput2" accept="image/*" />
      </div>
      <div className="flex items-center justify-end border-t border-strong p-4">
        <div className="flex gap-2">
          <Button mode="tertiary" onClick={() => handleCancel()}>
            Cancel
          </Button>
          <Button
            mode="primary"
            onClick={() => onSaveClick()}
            loading={isSaving}
            label="Save changes"
            disabled={typeof savedName === `undefined` || savedName.length === 0}
          />
        </div>
      </div>
    </div>
  )
}

export default ImageCropModalContent
